import React from "react";
import * as UI from "./ui.jsx";
import { authorizedFetch } from "./boot.js";
// Vendor Stats — live Repository viewing statistics for one sale.
//
// The snapshot arrives from /ui/repository-stats@1, which holds the machine-to-machine
// credential server-side. Per the Repository integration guide a 200 is a complete
// replacement snapshot, lots are keyed by lotId with lotNumber as the display value,
// and a failed poll keeps the last good snapshot and marks it stale rather than
// showing zero activity.

const { useState: useStateSt, useEffect: useEffectSt, useMemo: useMemoSt } = React;

function StatsView() {
  const { Card, Tag, Icon } = UI;
  const cfg = window.VD.repositoryStats || {};
  const [snap, setSnap] = useStateSt(null);
  const [loading, setLoading] = useStateSt(true);
  const [sort, setSort] = useStateSt("xray");
  const [query, setQuery] = useStateSt("");

  useEffectSt(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // The route is gated like every other, and this poll outlives the
        // access token that started it, so each attempt carries a fresh one.
        const res = await authorizedFetch("/ui/repository-stats@1");
        if (res.status === 401 || res.status === 403) {
          // Refused rather than unavailable: the snapshot is not stale data to
          // keep showing, and a retry loop cannot fix it.
          if (!cancelled) { setSnap({ source: "error", reason: "ACCESS_DENIED" }); setLoading(false); }
          return;
        }
        const body = await res.json();
        if (!cancelled) { setSnap(body); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setSnap({ source: "error", reason: "BROWSER_FETCH_FAILED" }); setLoading(false); }
      }
    };
    load();
    const every = Math.max(30, cfg.pollSeconds || 300) * 1000;
    const id = setInterval(load, every);
    return () => { cancelled = true; clearInterval(id); };
  }, [cfg.pollSeconds]);

  const lots = useMemoSt(() => {
    const rows = (snap && snap.lots) || [];
    const q = query.trim().toLowerCase();
    return rows
      .filter(l => !q || String(l.lotNumber || "").toLowerCase().includes(q) || String(l.lotId || "").toLowerCase().includes(q))
      .slice()
      .sort((a, b) => {
        if (sort === "lot") return String(a.lotNumber || "").localeCompare(String(b.lotNumber || ""), undefined, { numeric: true });
        if (sort === "scope") return (b.scopeViews || 0) - (a.scopeViews || 0);
        if (sort === "report") return (b.reportViews || 0) - (a.reportViews || 0);
        if (sort === "vets") return (b.uniqueVets || 0) - (a.uniqueVets || 0);
        return (b.xrayViews || 0) - (a.xrayViews || 0);
      });
  }, [snap, sort, query]);

  if (loading) {
    return <div className="fade-in modulegate"><div className="eyebrow">Vendor stats</div><div className="display" style={{ marginTop: 6 }}>Loading snapshot…</div></div>;
  }

  // Nothing configured: say exactly what is missing rather than showing an empty table.
  if (!snap || snap.source === "unconfigured") {
    return (
      <div className="fade-in modulegate">
        <div className="eyebrow">No credential configured</div>
        <div className="display" style={{ marginTop: 6 }}>Repository stats</div>
        <p>
          The Repository viewing-statistics pull needs its Auth0 machine-to-machine
          credential. Vendor Dashboard owns that client ID and secret; they are read from
          the environment at run time and never stored in this repository.
        </p>
        {(cfg.missing || []).length > 0 && (
          <p className="caption" style={{ marginTop: 10 }}>
            Not set: {(cfg.missing || []).map(m => <span key={m} className="mono">{m} </span>)}
          </p>
        )}
      </div>
    );
  }

  if (snap.source === "error") {
    return (
      <div className="fade-in modulegate">
        <div className="eyebrow">Snapshot unavailable</div>
        <div className="display" style={{ marginTop: 6 }}>Repository stats</div>
        <p>
          No snapshot has been read yet for sale <span className="mono">{snap.saleId}</span>.
          Reason <span className="mono">{snap.reason}</span>{snap.status ? <> · HTTP <span className="mono">{snap.status}</span></> : null}.
        </p>
        <p className="caption" style={{ marginTop: 10 }}>
          {snap.status === 403
            ? "A valid token whose subject is not the approved Vendor Dashboard subject returns 403."
            : snap.status === 404
              ? "Confirm the Inglis sale code exists in Repository."
              : "Transport, 429 and 5xx faults retry with bounded backoff; 400/401/403/404 do not."}
        </p>
      </div>
    );
  }

  const totals = snap.totals || {};
  const ms = (v) => (v == null ? "—" : `${Math.round(v / 1000).toLocaleString()}s`);
  const n = (v) => (v == null ? "—" : Number(v).toLocaleString());

  return (
    <div className="fade-in">
      {snap.stale && (
        <div className="card" style={{ borderColor: "var(--race-gold)", background: "#FBF6EA", marginBottom: 18 }}>
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <Tag variant="warn">Stale</Tag>
            <span style={{ fontSize: 14 }}>
              The last poll failed (<span className="mono">{snap.reason}</span>
              {snap.status ? <> · HTTP {snap.status}</> : null}). Showing the last good snapshot
              rather than zeroes.
            </span>
          </div>
        </div>
      )}

      <div className="card qstrip" style={{ padding: 0, display: "flex" }}>
        <Totals label="X-ray views" value={n(totals.xrayViews)} sub={ms(totals.xrayDurationMs)}/>
        <Totals label="Scope views" value={n(totals.scopeViews)} sub={ms(totals.scopeDurationMs)}/>
        <Totals label="Report views" value={n(totals.reportViews)} sub={ms(totals.reportDurationMs)}/>
        <Totals label="Unique vets" value={n(totals.uniqueVets)}/>
        <Totals label="PDF acks" value={n(totals.pdfAcknowledgements)}/>
      </div>

      <Card
        title={`Sale ${snap.saleId}`}
        subtitle={snap.countSemantics ? `Counts: ${snap.countSemantics.replace(/_/g, " ")} · durations: ${(snap.durationSemantics || "").replace(/_/g, " ")}` : undefined}
        right={<span className="row" style={{ gap: 8 }}>
          <Tag variant={snap.stale ? "warn" : "live"}>{snap.stale ? "Stale" : "Live"}</Tag>
          <span className="muted" style={{ fontSize: 11 }}>Repository · {snap.apiVersion}</span>
        </span>}
        style={{ marginTop: 22 }}
      >
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            Generated {snap.generatedAt || "—"} · {(snap.lots || []).length} catalogue lots · polling every {cfg.pollSeconds}s
          </div>
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <div className="searchbox" style={{ width: 190 }}>
              <Icon.search style={{ width: 15, height: 15, color: "var(--fg-muted)" }}/>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Lot…"
                     style={{ flex: 1, background: "transparent", border: 0, outline: 0, fontSize: 14 }}/>
            </div>
            <div className="seg">
              {[["xray", "X-ray"], ["scope", "Scope"], ["report", "Report"], ["vets", "Vets"], ["lot", "Lot"]].map(([id, lbl]) => (
                <button key={id} className={sort === id ? "on" : ""} onClick={() => setSort(id)}>{lbl}</button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card className="flush" style={{ marginTop: 18, overflowX: "auto" }}>
        <table className="table">
          <thead><tr>
            <th>Lot</th>
            <th style={{ textAlign: "right" }}>X-ray</th>
            <th style={{ textAlign: "right" }}>Scope</th>
            <th style={{ textAlign: "right" }}>Report</th>
            <th style={{ textAlign: "right" }}>Vets</th>
            <th style={{ textAlign: "right" }}>PDF acks</th>
            <th style={{ textAlign: "right" }}>Viewing time</th>
            <th>Last activity</th>
          </tr></thead>
          <tbody>
            {lots.length === 0 && (
              <tr><td colSpan="8" style={{ textAlign: "center", padding: 36, color: "var(--fg-muted)" }}>
                {query ? "No lot matches that search." : "The catalogue returned no lots."}
              </td></tr>
            )}
            {lots.map(lot => {
              const total = (lot.xrayDurationMs || 0) + (lot.scopeDurationMs || 0) + (lot.reportDurationMs || 0);
              const quiet = !lot.xrayViews && !lot.scopeViews && !lot.reportViews;
              return (
                <tr key={lot.lotId} style={quiet ? { opacity: 0.55 } : undefined}>
                  <td style={{ fontFamily: "var(--font-display)", color: "var(--inglis-blue)", fontSize: 16 }}>{lot.lotNumber || lot.lotId}</td>
                  <td style={{ textAlign: "right", color: "var(--fg-1)" }}>{n(lot.xrayViews)}</td>
                  <td style={{ textAlign: "right" }}>{n(lot.scopeViews)}</td>
                  <td style={{ textAlign: "right" }}>{n(lot.reportViews)}</td>
                  <td style={{ textAlign: "right" }}>{n(lot.uniqueVets)}</td>
                  <td style={{ textAlign: "right" }}>{n(lot.pdfAcknowledgements)}</td>
                  <td style={{ textAlign: "right" }} className="mono">{total ? ms(total) : "—"}</td>
                  <td className="mono muted" style={{ fontSize: 12 }}>{lot.lastActivityAt || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Totals({ label, value, sub }) {
  return (
    <div className="qstat">
      <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--fg-muted)", fontWeight: 700 }}>{label}</div>
      <div className="num" style={{ fontSize: 34, color: "var(--inglis-blue)", marginTop: 6 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub} viewing</div>}
    </div>
  );
}

export { StatsView };
